import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProjectScheduleTab } from '@/components/schedule/ProjectScheduleTab';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Home, Building2, Phone, Mail, MapPin, Calendar, 
  PoundSterling, Clock, CheckCircle, AlertCircle, Pause, XCircle, Edit, Trash2, X,
  Users, FileText, Camera, MessageSquare, AlertTriangle, Plus, Check,
  ExternalLink, Upload, Download, ClipboardList, Hammer, Package, Send,
  PhoneCall, MessageCircle, Car, Eye, Loader2, ShoppingCart
} from 'lucide-react';

import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';

// Status configurations
const PROJECT_STATUSES = {
  upcoming: { label: 'Upcoming', color: '#3B82F6', bgColor: 'bg-blue-100', textColor: 'text-blue-800', icon: Calendar },
  in_progress: { label: 'In Progress', color: '#F59E0B', bgColor: 'bg-amber-100', textColor: 'text-amber-800', icon: Clock },
  completed: { label: 'Completed', color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-800', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: '#6B7280', bgColor: 'bg-gray-100', textColor: 'text-gray-800', icon: Pause },
  cancelled: { label: 'Cancelled', color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-800', icon: XCircle }
};

// Material-quote status pill colour mapping. Replaces a nested ternary
// in the Materials tab below.
const MATERIAL_QUOTE_STATUS_BADGE = {
  approved: 'bg-green-100 text-green-800',
  sent:     'bg-blue-100 text-blue-800',
};
const MATERIAL_QUOTE_STATUS_BADGE_DEFAULT = 'bg-amber-100 text-amber-800';

// Section-specific photo categories (used by Photos tab for Approved Installer evidence)
const PHOTO_CATEGORIES_BY_SECTION = {
  probound: ['excavation_subbase', 'borders_extras', 'base', 'surface'],
  proepoxy: ['preparation', 'system'],
  prodecor: ['preparation', 'system'],
};
const PHOTO_CATEGORY_LABELS = {
  excavation_subbase: 'Excavation & Sub-base',
  borders_extras: 'Borders & Extras',
  base: 'Base',
  surface: 'Surface',
  preparation: 'Preparation',
  system: 'System',
  before: 'Before',
  during: 'During',
  after: 'After',
  issue: 'Issue',
  other: 'Other',
};

// Document categories for the Site Records document library. `rams` is a
// convenience shortcut for a combined RAMS pack; installers who track risk
// assessments and method statements separately can use those two.
const DOCUMENT_CATEGORIES = [
  { value: 'rams',             label: 'RAMS Pack' },
  { value: 'risk_assessment',  label: 'Risk Assessment' },
  { value: 'method_statement', label: 'Method Statement' },
  { value: 'quote',            label: 'Quote' },
  { value: 'invoice',          label: 'Invoice' },
  { value: 'spec',             label: 'Specification' },
  { value: 'terms',            label: 'Terms & Conditions' },
  { value: 'certificate',      label: 'Certificate' },
  { value: 'warranty',         label: 'Warranty' },
  { value: 'other',            label: 'Other' },
];
const DOCUMENT_CATEGORY_LABELS = Object.fromEntries(DOCUMENT_CATEGORIES.map(c => [c.value, c.label]));

// Stage icon mapping
const STAGE_ICONS = {
  clipboard: ClipboardList,
  'file-text': FileText,
  'pound-sterling': PoundSterling,
  package: Package,
  calendar: Calendar,
  hammer: Hammer,
  'check-circle': CheckCircle,
  circle: Clock
};

const ProjectDetail = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCommModal, setShowCommModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoType, setPhotoType] = useState('other');
  const [documentType, setDocumentType] = useState('rams');
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_type: 'deposit', payment_method: '', date: '', notes: '' });
  const [commForm, setCommForm] = useState({ type: 'call', summary: '', notes: '', date: '' });
  const [issueForm, setIssueForm] = useState({ issue_type: '', description: '', severity: 'medium', date: '' });
  const [stageForm, setStageForm] = useState({ name: '', icon: 'circle' });
  const [materialQuotes, setMaterialQuotes] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProject(data);
        // Default the photo upload category to the first one for this section
        const sectionCats = PHOTO_CATEGORIES_BY_SECTION[data.service_type] || [];
        if (sectionCats.length > 0) setPhotoType(sectionCats[0]);
        setEditForm({
          status: data.status,
          project_value: data.project_value,
          install_date: data.install_date || '',
          install_duration_days: data.install_duration_days || 1,
          notes: data.notes || '',
          payment_required_before_order: data.payment_required_before_order,
          project_type: data.project_type || '',
          project_size_m2: data.project_size_m2 ?? '',
          surface_colour: data.surface_colour || '',
          border_colour_type: data.border_colour_type || '',
          base_opted_for: data.base_opted_for || '',
          resin_system: data.resin_system || '',
        });
      }
    } catch (error) {
      logger.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchMaterialQuotes = useCallback(async () => {
    setLoadingMaterials(true);
    try {
      const response = await fetch(`${API_URL}/api/quotes?project_id=${projectId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        setMaterialQuotes(await response.json());
      }
    } catch (error) {
      logger.error('Error fetching material quotes:', error);
    } finally {
      setLoadingMaterials(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    if (activeTab === 'materials' && projectId) {
      fetchMaterialQuotes();
    }
  }, [activeTab, projectId, fetchMaterialQuotes]);

  const handleUpdateProject = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        setShowEditModal(false);
        fetchProject();
      }
    } catch (error) {
      logger.error('Error updating project:', error);
    }
  };

  const handleToggleStage = async (stageId) => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/stages/${stageId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchProject();
      }
    } catch (error) {
      logger.error('Error toggling stage:', error);
    }
  };

  const handleAddPayment = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...paymentForm,
          amount: parseFloat(paymentForm.amount)
        })
      });
      if (response.ok) {
        setShowPaymentModal(false);
        setPaymentForm({ amount: '', payment_type: 'deposit', payment_method: '', date: '', notes: '' });
        fetchProject();
      }
    } catch (error) {
      logger.error('Error adding payment:', error);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await fetch(`${API_URL}/api/projects/${projectId}/payments/${paymentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchProject();
    } catch (error) {
      logger.error('Error deleting payment:', error);
    }
  };

  const handleAddCommunication = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/communications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(commForm)
      });
      if (response.ok) {
        setShowCommModal(false);
        setCommForm({ type: 'call', summary: '', notes: '', date: '' });
        fetchProject();
      }
    } catch (error) {
      logger.error('Error adding communication:', error);
    }
  };

  const handleAddIssue = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/issues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(issueForm)
      });
      if (response.ok) {
        setShowIssueModal(false);
        setIssueForm({ issue_type: '', description: '', severity: 'medium', date: '' });
        fetchProject();
      }
    } catch (error) {
      logger.error('Error adding issue:', error);
    }
  };

  const handleResolveIssue = async (issueId) => {
    try {
      await fetch(`${API_URL}/api/projects/${projectId}/issues/${issueId}/resolve`, {
        method: 'POST',
        credentials: 'include'
      });
      fetchProject();
    } catch (error) {
      logger.error('Error resolving issue:', error);
    }
  };

  const handleAddStage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/stages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(stageForm)
      });
      if (response.ok) {
        setShowStageModal(false);
        setStageForm({ name: '', icon: 'circle' });
        fetchProject();
      }
    } catch (error) {
      logger.error('Error adding stage:', error);
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('Delete this stage?')) return;
    try {
      await fetch(`${API_URL}/api/projects/${projectId}/stages/${stageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchProject();
    } catch (error) {
      logger.error('Error deleting stage:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/photos?photo_type=${photoType}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );
      if (response.ok) {
        fetchProject();
      }
    } catch (error) {
      logger.error('Error uploading photo:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await fetch(`${API_URL}/api/projects/${projectId}/photos/${photoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      fetchProject();
    } catch (error) {
      logger.error('Error deleting photo:', error);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/documents?document_type=${encodeURIComponent(documentType)}`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );
      if (response.ok) {
        fetchProject();
      }
    } catch (error) {
      logger.error('Error uploading document:', error);
    } finally {
      setUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStageIcon = (iconName) => {
    const Icon = STAGE_ICONS[iconName] || Clock;
    return Icon;
  };

  const isStageCompleted = (stageId) => {
    return project?.stage_progress?.some(sp => sp.stage_id === stageId && sp.completed);
  };

  const getStageProgress = (stageId) => {
    return project?.stage_progress?.find(sp => sp.stage_id === stageId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center">
        <div className="text-gray-500">Loading project...</div>
      </div>
    );
  }

  const backToProjects = `/${project?.service_type || 'probound'}/projects`;

  if (!project) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Project not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const StatusIcon = PROJECT_STATUSES[project.status]?.icon || Clock;

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-4 border-[#2F5E96]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            onClick={() => navigate(backToProjects)}
            variant="outline"
            className="border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/10 font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/0yxgnasw_IMG_8077.jpeg" 
            alt="Ox Resin" 
            className="h-12 md:h-14"
          />
        </div>
      </header>

      {/* Project Header */}
      <div className="bg-gradient-to-r from-[#300122] to-[#4a0636] text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-mono text-white/70">{project.reference}</span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${PROJECT_STATUSES[project.status]?.bgColor} ${PROJECT_STATUSES[project.status]?.textColor}`}>
                  <StatusIcon className="w-3 h-3" />
                  {PROJECT_STATUSES[project.status]?.label}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold">{project.customer_name}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-white/80 text-sm">
                {project.customer_phone && (
                  <a href={`tel:${project.customer_phone}`} className="flex items-center gap-1 hover:text-white">
                    <Phone className="w-4 h-4" />
                    {project.customer_phone}
                  </a>
                )}
                {project.customer_email && (
                  <a href={`mailto:${project.customer_email}`} className="flex items-center gap-1 hover:text-white">
                    <Mail className="w-4 h-4" />
                    {project.customer_email}
                  </a>
                )}
                {project.customer_address && (
                  <a 
                    href={`https://maps.google.com/?q=${encodeURIComponent(project.customer_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 hover:text-white"
                  >
                    <MapPin className="w-4 h-4" />
                    {project.customer_address}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                data-testid="send-approval-letter-btn"
                onClick={async () => {
                  if (!window.confirm(`Send the "Ox Approved Installer" letter to ${project.customer_name} (${project.customer_email})?`)) return;
                  try {
                    const res = await fetch(`${API_URL}/api/projects/${projectId}/send-approval-letter`, {
                      method: 'POST', credentials: 'include',
                    });
                    if (!res.ok) {
                      const err = await res.json();
                      alert(formatApiError(err) || 'Failed to send');
                    } else {
                      alert('Approval letter sent.');
                    }
                  } catch (e) {
                    alert('Failed to send');
                  }
                }}
                variant="outline"
                className="w-full sm:w-auto border-emerald-300 text-emerald-100 hover:bg-emerald-600/20 whitespace-nowrap"
                title="Sends an email to the customer explaining what 'Ox Approved' means and the project guarantee"
              >
                <Send className="w-4 h-4 mr-2 shrink-0" />
                Send Approval Letter
              </Button>
              <Button
                onClick={() => {
                  const product = (project.service_type || 'probound');
                  const orderPath = product === 'probound' ? '/order' : `/${product}/order`;
                  navigate(`${orderPath}?project=${project.id}`);
                }}
                variant="outline"
                className="w-full sm:w-auto border-amber-300 text-amber-100 hover:bg-amber-600/20 whitespace-nowrap"
                title="Pre-fills the material order builder with this project's surfacing details so you don't have to retype them"
                data-testid="generate-order-from-project"
              >
                <Package className="w-4 h-4 mr-2 shrink-0" />
                Generate Order
              </Button>
              <Button
                onClick={() => setShowEditModal(true)}
                variant="outline"
                className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 whitespace-nowrap"
              >
                <Edit className="w-4 h-4 mr-2 shrink-0" />
                Edit Project
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="max-w-7xl mx-auto px-4 -mt-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
            <p className="text-xs text-gray-500">Project Value</p>
            <p className="text-2xl font-bold text-[#27292E]">{formatCurrency(project.project_value)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
            <p className="text-xs text-gray-500">Payments Received</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(project.total_paid)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
            <p className="text-xs text-gray-500">Outstanding</p>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(project.outstanding)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
            <p className="text-xs text-gray-500">Install Date</p>
            <p className="text-2xl font-bold text-[#2F5E96]">{project.install_date || 'TBC'}</p>
            {project.install_duration_days > 1 && (
              <p className="text-xs text-gray-400">{project.install_duration_days} days</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="flex gap-1 overflow-x-auto pb-2 border-b border-gray-200">
          {[
            { id: 'overview', label: 'Overview', icon: Building2 },
            { id: 'stages', label: 'Stages', icon: CheckCircle },
            { id: 'schedule', label: 'Schedule', icon: Calendar },
            { id: 'payments', label: 'Payments', icon: PoundSterling },
            { id: 'materials', label: 'Materials', icon: ShoppingCart },
            { id: 'photos', label: 'Photos', icon: Camera },
            { id: 'comms', label: 'Communications', icon: MessageSquare },
            { id: 'issues', label: 'Snagging', icon: AlertTriangle },
            { id: 'docs', label: 'Site Records', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 font-medium whitespace-nowrap transition-colors rounded-t-lg ${
                activeTab === tab.id
                  ? 'bg-white text-[#300122] border-b-2 border-[#300122]'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'issues' && project.issues?.filter(i => !i.resolved).length > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                  {project.issues.filter(i => !i.resolved).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Project Overview — surfacing meta */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid="project-overview-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-[#27292E]">Project Overview</h3>
                <Button size="sm" variant="outline" onClick={() => setShowEditModal(true)} data-testid="overview-edit-btn">
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { label: 'Project Type',    value: project.project_type,                            testid: 'meta-project-type' },
                  { label: 'Project Size',    value: project.project_size_m2 != null ? `${project.project_size_m2} m²` : null, testid: 'meta-project-size' },
                  { label: 'Surface Colour',  value: project.surface_colour,                          testid: 'meta-surface-colour' },
                  { label: 'Border Colour / Type', value: project.border_colour_type,                 testid: 'meta-border' },
                  { label: 'Base Opted For',  value: project.base_opted_for,                          testid: 'meta-base' },
                  { label: 'Resin System',    value: project.resin_system,                            testid: 'meta-resin-system' },
                ].map((row) => (
                  <div key={row.label} data-testid={row.testid}>
                    <dt className="text-xs uppercase tracking-wider text-gray-500 mb-0.5">{row.label}</dt>
                    <dd className={`text-sm font-semibold ${row.value ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                      {row.value || 'Not set — click Edit to add'}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Recent Payments */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-[#27292E]">Recent Payments</h3>
                <Button size="sm" onClick={() => setShowPaymentModal(true)} className="bg-[#300122] hover:bg-[#4a0636]">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              {project.payments?.length === 0 ? (
                <p className="text-gray-400 text-sm">No payments recorded yet</p>
              ) : (
                <div className="space-y-2">
                  {project.payments?.slice(0, 3).map(payment => (
                    <div key={payment.id} className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium text-green-600">{formatCurrency(payment.amount)}</span>
                        <span className="text-xs text-gray-400 ml-2">{payment.payment_type}</span>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(payment.date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            {project.notes && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-lg text-[#27292E] mb-2">Notes</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{project.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Stages Tab */}
        {activeTab === 'schedule' && (
          <ProjectScheduleTab project={project} readOnly={['admin', 'manager', 'team'].includes(user?.role) && project.installer_id !== user?.user_id} />
        )}

        {activeTab === 'stages' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <h3 className="font-bold text-lg text-[#27292E]">Project Stages</h3>
                <p className="text-xs text-gray-500 mt-0.5">Add, remove, or mark each stage as complete as work progresses.</p>
              </div>
              <Button size="sm" onClick={() => setShowStageModal(true)} className="bg-[#300122] hover:bg-[#4a0636]" data-testid="add-stage-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Stage
              </Button>
            </div>
            <div className="space-y-3">
              {project.stages?.map((stage) => {
                const completed = isStageCompleted(stage.id);
                const progress = getStageProgress(stage.id);
                const Icon = getStageIcon(stage.icon);

                return (
                  <div
                    key={stage.id}
                    data-testid={`stage-row-${stage.id}`}
                    className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border ${
                      completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        completed ? 'bg-green-500 text-white' : 'bg-white text-gray-400 border border-gray-200'
                      }`}>
                        {completed ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 truncate">{stage.name}</div>
                        {completed && progress?.completed_at && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            Completed {formatDate(progress.completed_at)}{progress.completed_by ? ` by ${progress.completed_by}` : ''}
                          </div>
                        )}
                        {progress?.notes && (
                          <div className="text-xs text-gray-600 mt-1 italic">{progress.notes}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 sm:flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleToggleStage(stage.id)}
                        data-testid={`stage-toggle-${stage.id}`}
                        className={completed
                          ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                          : 'bg-green-600 hover:bg-green-700 text-white'}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        {completed ? 'Mark incomplete' : 'Mark complete'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteStage(stage.id)}
                        data-testid={`stage-remove-${stage.id}`}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        title="Remove this stage"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="ml-1 hidden sm:inline">Remove</span>
                      </Button>
                    </div>
                  </div>
                );
              })}
              {(!project.stages || project.stages.length === 0) && (
                <div className="text-center py-12 text-gray-400">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No stages yet — click <strong>Add Stage</strong> to start.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-bold text-lg text-[#27292E]">Payment Tracking</h3>
                <p className="text-sm text-gray-500">
                  {project.payment_required_before_order && (
                    <span className="text-amber-600">⚠️ Payment required before materials can be ordered</span>
                  )}
                </p>
              </div>
              <Button onClick={() => setShowPaymentModal(true)} className="bg-[#300122] hover:bg-[#4a0636]">
                <Plus className="w-4 h-4 mr-2" />
                Add Payment
              </Button>
            </div>
            
            {/* Payment Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-xs text-gray-500">Project Value</p>
                <p className="text-xl font-bold">{formatCurrency(project.project_value)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Received</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(project.total_paid)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Outstanding</p>
                <p className="text-xl font-bold text-amber-600">{formatCurrency(project.outstanding)}</p>
              </div>
            </div>

            {/* Payment List */}
            {project.payments?.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <PoundSterling className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No payments recorded</p>
              </div>
            ) : (
              <div className="space-y-3">
                {project.payments?.map(payment => (
                  <div key={payment.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <PoundSterling className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-green-600">{formatCurrency(payment.amount)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            payment.payment_type === 'deposit' ? 'bg-blue-100 text-blue-700' :
                            payment.payment_type === 'final' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {payment.payment_type}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatDate(payment.date)} {payment.payment_method && `• ${payment.payment_method}`}
                        </div>
                        {payment.notes && <p className="text-sm text-gray-500 mt-1">{payment.notes}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeletePayment(payment.id)}
                      className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Materials Tab */}
        {activeTab === 'materials' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-lg text-[#27292E]">Material Quotes</h3>
              <Button
                onClick={() => navigate(`/order?project=${projectId}`)}
                className="bg-[#2F5E96] hover:bg-[#254b7a] text-white"
                data-testid="new-material-quote-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Get Material Quote
              </Button>
            </div>

            {loadingMaterials ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : materialQuotes.length > 0 ? (
              <div className="space-y-4">
                {materialQuotes.map(quote => (
                  <div key={quote.id} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-[#2F5E96] text-lg">{quote.reference}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${MATERIAL_QUOTE_STATUS_BADGE[quote.status] || MATERIAL_QUOTE_STATUS_BADGE_DEFAULT}`}>
                          {quote.status || 'Pending'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-400">
                        {new Date(quote.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </div>

                    {/* Blend Areas Summary */}
                    <div className="space-y-1.5 mb-3">
                      {(quote.blend_areas || []).map((area, i) => (
                        <div key={`${area.surface_blend}-${i}`} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                          <div>
                            <span className="font-medium text-gray-700">{area.surface_blend}</span>
                            <span className="text-gray-400 ml-2">({area.specification})</span>
                          </div>
                          <span className="font-medium">{area.quantity} {quote.measurement_type === 'Square metres (m²)' ? 'm²' : 'mixes'}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500 border-t border-gray-100 pt-3">
                      <div className="flex gap-4">
                        <span>Wastage: {quote.wastage}</span>
                        <span>Mesh: {quote.requires_mesh ? 'Yes' : 'No'}</span>
                        {quote.calculation?.total_m2 > 0 && (
                          <span>Total: {quote.calculation.total_m2} m²</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No material quotes yet</p>
                <p className="text-sm mt-1">Click &quot;Get Material Quote&quot; to start ordering materials for this project</p>
              </div>
            )}
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-lg text-[#27292E]">Site Photos</h3>
              <div className="flex items-center gap-2">
                <select
                  value={photoType}
                  onChange={(e) => setPhotoType(e.target.value)}
                  data-testid="photo-category-select"
                  className="border rounded-lg px-3 py-2 text-sm"
                >
                  {(PHOTO_CATEGORIES_BY_SECTION[project.service_type] || []).map(cat => (
                    <option key={cat} value={cat}>{PHOTO_CATEGORY_LABELS[cat] || cat}</option>
                  ))}
                  <option value="other">Other</option>
                  <option value="issue">Issue</option>
                </select>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#300122] hover:bg-[#4a0636]"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Photo
                </Button>
              </div>
            </div>

            {/* Photo Categories — section specific + generic fallback */}
            {[...(PHOTO_CATEGORIES_BY_SECTION[project.service_type] || []), 'issue', 'other'].map(type => {
              const photos = project.photos?.filter(p => p.photo_type === type) || [];
              if (photos.length === 0) return null;

              return (
                <div key={type} className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-3">
                    {PHOTO_CATEGORY_LABELS[type] || type} Photos ({photos.length})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {photos.map(photo => (
                      <div key={photo.id} className="relative group">
                        <img
                          src={`${API_URL}/api/projects/${projectId}/photos/${photo.id}`}
                          alt={photo.caption || photo.original_filename}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <a
                            href={`${API_URL}/api/projects/${projectId}/photos/${photo.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white rounded-full"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="p-2 bg-red-500 text-white rounded-full"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {project.photos?.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No photos uploaded yet</p>
                <p className="text-sm">Upload before, during, and after photos to document the project</p>
              </div>
            )}
          </div>
        )}

        {/* Communications Tab */}
        {activeTab === 'comms' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-lg text-[#27292E]">Communication Log</h3>
              <Button onClick={() => setShowCommModal(true)} className="bg-[#300122] hover:bg-[#4a0636]">
                <Plus className="w-4 h-4 mr-2" />
                Log Communication
              </Button>
            </div>

            {project.communications?.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No communications logged</p>
              </div>
            ) : (
              <div className="space-y-4">
                {project.communications?.slice().reverse().map(comm => {
                  const typeIcons = {
                    call: PhoneCall,
                    message: MessageCircle,
                    email: Mail,
                    site_visit: Car,
                    other: MessageSquare
                  };
                  const Icon = typeIcons[comm.type] || MessageSquare;
                  
                  return (
                    <div key={comm.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium capitalize">{comm.type.replace('_', ' ')}</span>
                          <span className="text-xs text-gray-400">{formatDate(comm.date)}</span>
                          <span className="text-xs text-gray-400">by {comm.logged_by_name}</span>
                        </div>
                        <p className="text-gray-700">{comm.summary}</p>
                        {comm.notes && <p className="text-sm text-gray-500 mt-1">{comm.notes}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Issues Tab — Snagging Log */}
        {activeTab === 'issues' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-[#27292E]">Snagging Log</h3>
                <p className="text-xs text-gray-500">Track defects and site issues from report through to resolution.</p>
              </div>
              <Button onClick={() => setShowIssueModal(true)} data-testid="snagging-add-btn" className="bg-[#300122] hover:bg-[#4a0636]">
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>

            {/* Snagging summary — outstanding/severity counts so staff see the shape at a glance. */}
            {project.issues?.length > 0 && (() => {
              const openIssues = project.issues.filter(i => !i.resolved);
              const highOpen = openIssues.filter(i => i.severity === 'high').length;
              const medOpen = openIssues.filter(i => i.severity === 'medium').length;
              const lowOpen = openIssues.filter(i => i.severity === 'low').length;
              const resolved = project.issues.length - openIssues.length;
              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5" data-testid="snagging-summary">
                  <div className="p-3 rounded-lg bg-rose-50 border border-rose-100">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">High priority</div>
                    <div className="text-2xl font-bold text-rose-700">{highOpen}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Medium</div>
                    <div className="text-2xl font-bold text-amber-700">{medOpen}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600">Low</div>
                    <div className="text-2xl font-bold text-gray-700">{lowOpen}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Resolved</div>
                    <div className="text-2xl font-bold text-emerald-700">{resolved}</div>
                  </div>
                </div>
              );
            })()}

            {project.issues?.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No issues reported</p>
              </div>
            ) : (
              <div className="space-y-4">
                {project.issues?.map(issue => (
                  <div key={issue.id} className={`p-4 rounded-lg border-l-4 ${
                    issue.resolved 
                      ? 'bg-green-50 border-green-500' 
                      : issue.severity === 'high' 
                        ? 'bg-red-50 border-red-500'
                        : issue.severity === 'medium'
                          ? 'bg-amber-50 border-amber-500'
                          : 'bg-gray-50 border-gray-400'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{issue.issue_type}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            issue.resolved ? 'bg-green-100 text-green-700' :
                            issue.severity === 'high' ? 'bg-red-100 text-red-700' :
                            issue.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {issue.resolved ? 'Resolved' : issue.severity}
                          </span>
                        </div>
                        <p className="text-gray-700">{issue.description}</p>
                        <p className="text-xs text-gray-400 mt-2">
                          Reported {formatDate(issue.date)} by {issue.reported_by_name}
                        </p>
                        {issue.resolved && issue.resolution_notes && (
                          <p className="text-sm text-green-700 mt-2 bg-green-100 p-2 rounded">
                            Resolution: {issue.resolution_notes}
                          </p>
                        )}
                      </div>
                      {!issue.resolved && (
                        <Button
                          onClick={() => handleResolveIssue(issue.id)}
                          size="sm"
                          variant="outline"
                          className="border-green-500 text-green-600 hover:bg-green-50"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'docs' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div>
                <h3 className="font-bold text-lg text-[#27292E]">Site Records &amp; Documents</h3>
                <p className="text-xs text-gray-500">RAMS, risk assessments, method statements, certificates and other project paperwork.</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  data-testid="document-category-select"
                  className="border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <input
                  type="file"
                  ref={docInputRef}
                  onChange={handleDocUpload}
                  className="hidden"
                />
                <Button
                  onClick={() => docInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-document-btn"
                  className="bg-[#300122] hover:bg-[#4a0636]"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload
                </Button>
              </div>
            </div>

            {project.documents?.length === 0 || !project.documents ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No documents uploaded</p>
                <p className="text-sm mt-1">Upload RAMS packs, risk assessments and site paperwork here.</p>
              </div>
            ) : (
              // Group documents by their document_type so RAMS / Risk Assessments etc.
              // each get their own visual bucket. Categories without docs are hidden.
              DOCUMENT_CATEGORIES.map(cat => {
                const docs = (project.documents || []).filter(d => (d.document_type || 'other') === cat.value);
                if (docs.length === 0) return null;
                return (
                  <div key={cat.value} className="mb-6" data-testid={`document-group-${cat.value}`}>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      {cat.label} <span className="text-gray-400 font-normal">({docs.length})</span>
                    </h4>
                    <div className="space-y-2">
                      {docs.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`document-row-${doc.id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="w-6 h-6 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                              <p className="text-xs text-gray-400">
                                {DOCUMENT_CATEGORY_LABELS[doc.document_type] || doc.document_type} · uploaded {formatDate(doc.created_at)}{doc.uploaded_by_name ? ` by ${doc.uploaded_by_name}` : ''}
                              </p>
                            </div>
                          </div>
                          <a
                            href={`${API_URL}/api/projects/${projectId}/documents/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded shrink-0"
                            aria-label={`Download ${doc.original_filename}`}
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Edit Project</h3>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-2.5"
                >
                  {Object.entries(PROJECT_STATUSES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Project Value (£)</Label>
                <Input
                  type="number"
                  value={editForm.project_value}
                  onChange={(e) => setEditForm({ ...editForm, project_value: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Install Date</Label>
                <Input
                  type="date"
                  value={editForm.install_date}
                  onChange={(e) => setEditForm({ ...editForm, install_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={editForm.install_duration_days}
                  onChange={(e) => setEditForm({ ...editForm, install_duration_days: parseInt(e.target.value) || 1 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-3 min-h-[80px]"
                />
              </div>

              {/* Surfacing details — shown on Overview */}
              <div className="pt-4 border-t border-gray-200">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Project Overview</div>
                <div className="space-y-3">
                  <div>
                    <Label>Project Type</Label>
                    <select
                      value={editForm.project_type || ''}
                      onChange={(e) => setEditForm({ ...editForm, project_type: e.target.value })}
                      className="mt-1 w-full border-2 rounded-lg p-2.5"
                      data-testid="edit-project-type"
                    >
                      <option value="">—</option>
                      <option value="Driveway">Driveway</option>
                      <option value="Patio">Patio</option>
                      <option value="Pathway">Pathway</option>
                      <option value="Pool Surround">Pool Surround</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label>Project Size (m²)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={editForm.project_size_m2 ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, project_size_m2: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      className="mt-1"
                      data-testid="edit-project-size-m2"
                    />
                  </div>
                  <div>
                    <Label>Surface Colour</Label>
                    <Input
                      type="text"
                      value={editForm.surface_colour || ''}
                      onChange={(e) => setEditForm({ ...editForm, surface_colour: e.target.value })}
                      className="mt-1"
                      data-testid="edit-surface-colour"
                    />
                  </div>
                  <div>
                    <Label>Border Colour / Type</Label>
                    <Input
                      type="text"
                      value={editForm.border_colour_type || ''}
                      onChange={(e) => setEditForm({ ...editForm, border_colour_type: e.target.value })}
                      className="mt-1"
                      data-testid="edit-border"
                    />
                  </div>
                  <div>
                    <Label>Base Opted For</Label>
                    <Input
                      type="text"
                      value={editForm.base_opted_for || ''}
                      onChange={(e) => setEditForm({ ...editForm, base_opted_for: e.target.value })}
                      className="mt-1"
                      placeholder="e.g. MOT Type 1 / Open-grade etc."
                      data-testid="edit-base"
                    />
                  </div>
                  <div>
                    <Label>Resin System</Label>
                    <select
                      value={editForm.resin_system || ''}
                      onChange={(e) => setEditForm({ ...editForm, resin_system: e.target.value })}
                      className="mt-1 w-full border-2 rounded-lg p-2.5"
                      data-testid="edit-resin-system"
                    >
                      <option value="">—</option>
                      <option value="Standard">Standard</option>
                      <option value="Ultra">Ultra</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="paymentRequired"
                  checked={editForm.payment_required_before_order}
                  onChange={(e) => setEditForm({ ...editForm, payment_required_before_order: e.target.checked })}
                />
                <Label htmlFor="paymentRequired">Payment required before materials order</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleUpdateProject} className="flex-1 bg-[#300122] hover:bg-[#4a0636]">Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Add Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Amount (£) *</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Payment Type</Label>
                <select
                  value={paymentForm.payment_type}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-2.5"
                >
                  <option value="deposit">Deposit</option>
                  <option value="mid">Mid Payment</option>
                  <option value="final">Final Payment</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label>Payment Method</Label>
                <Input
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  placeholder="e.g. Bank Transfer, Card, Cash"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-3 min-h-[60px]"
                  placeholder="Optional notes..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowPaymentModal(false)} className="flex-1">Cancel</Button>
                <Button 
                  onClick={handleAddPayment} 
                  disabled={!paymentForm.amount || !paymentForm.date}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Add Payment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Communication Modal */}
      {showCommModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Log Communication</h3>
              <button onClick={() => setShowCommModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Type</Label>
                <select
                  value={commForm.type}
                  onChange={(e) => setCommForm({ ...commForm, type: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-2.5"
                >
                  <option value="call">Phone Call</option>
                  <option value="message">Message</option>
                  <option value="email">Email</option>
                  <option value="site_visit">Site Visit</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label>Summary *</Label>
                <Input
                  value={commForm.summary}
                  onChange={(e) => setCommForm({ ...commForm, summary: e.target.value })}
                  placeholder="Brief summary of the communication"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={commForm.date}
                  onChange={(e) => setCommForm({ ...commForm, date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  value={commForm.notes}
                  onChange={(e) => setCommForm({ ...commForm, notes: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-3 min-h-[60px]"
                  placeholder="Additional details..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCommModal(false)} className="flex-1">Cancel</Button>
                <Button 
                  onClick={handleAddCommunication} 
                  disabled={!commForm.summary || !commForm.date}
                  className="flex-1 bg-[#300122] hover:bg-[#4a0636]"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Issue Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Report Issue</h3>
              <button onClick={() => setShowIssueModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Issue Type *</Label>
                <Input
                  value={issueForm.issue_type}
                  onChange={(e) => setIssueForm({ ...issueForm, issue_type: e.target.value })}
                  placeholder="e.g. Snagging, Damage, Customer Complaint"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <textarea
                  value={issueForm.description}
                  onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-3 min-h-[80px]"
                  placeholder="Describe the issue..."
                />
              </div>
              <div>
                <Label>Severity</Label>
                <select
                  value={issueForm.severity}
                  onChange={(e) => setIssueForm({ ...issueForm, severity: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-2.5"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={issueForm.date}
                  onChange={(e) => setIssueForm({ ...issueForm, date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowIssueModal(false)} className="flex-1">Cancel</Button>
                <Button 
                  onClick={handleAddIssue} 
                  disabled={!issueForm.issue_type || !issueForm.description || !issueForm.date}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Report Issue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Stage Modal */}
      {showStageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Add Stage</h3>
              <button onClick={() => setShowStageModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Stage Name *</Label>
                <Input
                  value={stageForm.name}
                  onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
                  placeholder="e.g. Final Inspection"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Icon</Label>
                <select
                  value={stageForm.icon}
                  onChange={(e) => setStageForm({ ...stageForm, icon: e.target.value })}
                  className="mt-1 w-full border-2 rounded-lg p-2.5"
                >
                  <option value="circle">Default</option>
                  <option value="clipboard">Clipboard</option>
                  <option value="file-text">Document</option>
                  <option value="pound-sterling">Payment</option>
                  <option value="package">Package</option>
                  <option value="calendar">Calendar</option>
                  <option value="hammer">Work</option>
                  <option value="check-circle">Complete</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowStageModal(false)} className="flex-1">Cancel</Button>
                <Button 
                  onClick={handleAddStage} 
                  disabled={!stageForm.name}
                  className="flex-1 bg-[#300122] hover:bg-[#4a0636]"
                >
                  Add Stage
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
